import { clerkClient } from "@/App";
import { api } from "./api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

type Attrs = Record<string, string | number | object>;

export interface Sensor {
  sensor_id: string;
  organization_id?: string | null;
  sensor_name: string;
  sensor_type: string;
  latitude: number;
  longitude: number;
  details: Attrs;
  created_at: Date;
  online_at?: Date | null;
  deleted_at?: Date | null;
}

interface SensorsResponse {
  sensors: Sensor[];
  total: number;
}

// fetch all sensors
export const fetchSensorsQuery = async () => {
  const authToken = await clerkClient.session?.getToken();

  const resp = await api.get("/v0/sensors", {
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
  });

  return resp.data as SensorsResponse;
};

export const useSensorsQuery = () => {
  return useQuery({
    queryKey: ["sensors"],
    queryFn: fetchSensorsQuery,
  });
};

// fetch a single sensor
export const fetchSensorQuery = async (sensorId: string) => {
  const authToken = await clerkClient.session?.getToken();

  const resp = await api.get(`/v0/sensors/${sensorId}`, {
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
  });

  return resp.data as Sensor;
};

export const useSensorQuery = (sensorId: string) => {
  return useQuery({
    queryKey: ["sensor", sensorId],
    queryFn: () => fetchSensorQuery(sensorId),
    enabled: !!sensorId, // Only run this query if sensorId is provided
  });
};

// Update sensor position
export const updateSensorQuery = async ({
  sensorId,
  latitude,
  longitude,
  name,
}: {
  sensorId: string;
  latitude?: number;
  longitude?: number;
  name?: string;
}) => {
  const authToken = await clerkClient.session?.getToken();

  await api.patch(
    `/v0/sensors/${sensorId}`,
    {
      latitude,
      longitude,
      sensorName: name,
    },
    {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    }
  );
};

export const useUpdateSensorQuery = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateSensorQuery,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sensors"] });
      queryClient.invalidateQueries({ queryKey: ["sensor"] });
    },
  });
};
